import React from "react";
import { useParams, Link } from "react-router-dom";
import useFetchRecipe from "../hooks/useFetchRecipe";
import LoadIcon from "../img/icon/loading.gif";
import { useEffect } from "react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { useLanguage } from "../i18n/LanguageContext";

const Recipe = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const { data: recipe, isLoading, isError } = useFetchRecipe(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-200 flex justify-center items-center w-full">
        <img src={LoadIcon} alt="" className="h-2/4" />
      </div>
    );
  }

  if (isError || !recipe) {
    return (
      <div className="h-screen bg-gray-200 flex flex-col justify-center items-center px-6 text-center">
        <h1 className="text-2xl font-bold text-gray-700 mb-4">
          {t("common.recipeNotFound")}
        </h1>
        <p className="text-gray-600 mb-6">{t("recipe.notFoundText")}</p>
        <Link
          to="/"
          className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          {t("common.backToHome")}
        </Link>
      </div>
    );
  }

  const recipeImage = recipe.image
    ? recipe.image
    : "https://via.placeholder.com/640x360?text=No+Image";

  const ingredients = recipe.extendedIngredients ?? [];
  const instructionGroups = (recipe.analyzedInstructions ?? []).filter(
    (instruction) => instruction.steps && instruction.steps.length > 0
  );
  const plainInstructions = recipe.instructions
    ? recipe.instructions.replace(/<[^>]*>/g, "").trim()
    : "";
  const hasCookingProcess =
    instructionGroups.length > 0 || plainInstructions.length > 0;

  return (
    <>
      <NavBar />
      <div className="container min-h-screen mx-auto px-6 py-24">
        <div className="flex flex-wrap">
          {/* Recipe image */}
          <div className="w-full lg:w-2/5 mb-4">
            <img
              className="w-full h-auto rounded-lg"
              src={recipeImage}
              alt={recipe.title}
            />
          </div>
          {/* Recipe details */}
          <div className="w-full lg:w-3/5 lg:pl-6">
            {/* Recipe title */}
            <h1 className="text-3xl font-bold md:mb-5 mb-2">{recipe.title}</h1>
            {/* Recipe summary */}
            {recipe.summary && (
              <div
                className="mb-4 indent-8 text-justify"
                dangerouslySetInnerHTML={{ __html: recipe.summary }}
              ></div>
            )}
            {/* Recipe info */}
            <div className="flex flex-wrap">
              <div className="w-full md:w-1/2 mb-4">
                <h2 className="font-bold mb-2">{t("recipe.ingredients")}</h2>
                <ul className="list-disc ml-6 pr-2">
                  {ingredients.map((ingredient, index) => (
                    <li key={`${ingredient.id}-${index}`}>
                      {ingredient.original}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-full md:w-1/2 mb-4">
                <h2 className="font-bold mb-2">{t("recipe.cookingTime")}</h2>
                <p>
                  {recipe.readyInMinutes} {t("recipe.minutes")}
                </p>
                <h2 className="font-bold mt-4 mb-2">{t("recipe.servings")}</h2>
                <p>{recipe.servings}</p>
              </div>
            </div>
            {/* Cooking process */}
            {hasCookingProcess && (
              <div className="mt-8">
                <h2 className="font-bold mb-4 text-2xl">
                  {t("recipe.cookingProcess")}
                </h2>
                {instructionGroups.length > 0 ? (
                  instructionGroups.map((instruction, index) => (
                    <div key={index} className="mb-4">
                      <h3 className="font-bold mb-2">{instruction.name}</h3>
                      <ol className="list-decimal ml-6">
                        {instruction.steps.map((step) => (
                          <li key={step.number}>{step.step}</li>
                        ))}
                      </ol>
                    </div>
                  ))
                ) : (
                  <p className="whitespace-pre-line">{plainInstructions}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Recipe;
